import { useRef, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'

import {
  trackCitizenshipCountryAddition,
  trackCitizenshipCountryRemoval,
  trackCitizenshipAddButton,
  trackCitizenshipAddition,
  trackCitizenshipEditButton,
  trackCitizenshipEdit,
  trackCitizenshipDeleteButton,
  trackCitizenshipDelete,
} from '../../../helpers/analyticsHelpers'
import { useFetchCountries } from '../../../hooks/useCountries'
import { CITIZENSHIP_KEY } from '../../../hooks/useCandidateProfile'
import { createCitizenshipStatus, updateCitizenshipStatus, deleteCitizenshipStatus, deleteManyCitizenshipStatuses } from './graphql'
import { createWorkAuthOnSuccess, updateWorkAuthOnSuccess, deleteWorkAuthOnSuccess } from '../helpers/onSuccessHelpers'
import {
  formatInitialValues,
  formatWorkAuthDataForCreate,
  formatCitizenshipDataForCreate,
  formatFormDataForUpdate,
  updateFormState,
} from '../helpers/formatHelpers'

const deepMatch = (arrayA, arrayB) => {
  const aValues = arrayA.sort().join('')
  const bValues = arrayB.sort().join('')
  return aValues === bValues
}

export const useCitizenship = ({ regionalCitizenships, candidateId, preferredCountries, reduxChangeUrl, nextUrl }) => {
  const queryClient = useQueryClient()
  const [newCitizenship, setNewCitizenship] = useState(false)
  const [editingCitizenshipIds, setEditingCitizenshipids] = useState([])
  const { isLoadingCountries, allCountries } = useFetchCountries()
  const [isLoadingSubmitNewWorkAuth, setIsLoadingSubmitNewWorkAuth] = useState(false)
  const [citizenshipsToDelete, setCitizenshipsToDelete] = useState([])

  const goToNextStep = () => reduxChangeUrl(nextUrl)

  const selectedCountries = regionalCitizenships.map(i => i.countryIso2Code)
  const { control, handleSubmit, getValues, formState, setError, clearErrors, reset, watch, setValue } = useForm({
    defaultValues: formatInitialValues({
      regionalCitizenshipsData: regionalCitizenships,
      shouldExcludeNoId: true,
    }),
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'regionalCitizenships',
  })

  const watchFieldArray = watch('regionalCitizenships')

  const controlledFields = fields
    .map((field, index) => {
      return {
        ...field,
        ...watchFieldArray[index],
      }
    })
    .filter(controlledField => controlledField.rcId)

  const { formError } = formState.errors
  const { errors } = formState

  const filteredCitizenshipCountryResults = useRef([])
  const [filteringCitizenshipCountries, setFilteringCitizenshipCountries] = useState(false)

  const { mutateAsync: createCitizenship, isLoading: isCreatingCitizenships } = useMutation(createCitizenshipStatus, {
    onSuccess: data => {
      createWorkAuthOnSuccess({
        data,
        queryClient,
        candidateId,
        citizenships: getValues().citizenships,
        reset,
        formatInitialValues,
        trackCitizenshipAddition,
        citizenshipsToDelete,
      })
    },
    onError: () => {
      setError('formError', {
        message: "We couldn't create the citizenship.",
      })
    },
  })

  const { mutate: updateCitizenship, isLoading: isUpdatingCitizenship } = useMutation(updateCitizenshipStatus, {
    onSuccess: data => {
      updateWorkAuthOnSuccess({
        data,
        editingCitizenshipIds,
        queryClient,
        candidateId,
        citizenships: getValues().citizenships,
        setEditingCitizenshipids,
        trackCitizenshipEdit,
        citizenshipsToDelete,
      })
    },
    onError: () => {
      setError('formError', {
        message: "We couldn't update the citizenship.",
      })
    },
  })

  const { mutate: deleteCitizenshipMutate, isLoading: isDeletingCitizenship } = useMutation(deleteCitizenshipStatus, {
    onSuccess: data => {
      deleteWorkAuthOnSuccess({
        data,
        queryClient,
        candidateId,
        reset,
        formatInitialValues,
        trackCitizenshipDelete,
        citizenships: getValues().citizenships,
        citizenshipsToDelete,
      })
    },
  })

  const { mutate: deleteCitizenships, isLoading: isDeletingCitizenships } = useMutation(deleteManyCitizenshipStatuses, {
    onSuccess: data => {
      deleteWorkAuthOnSuccess({
        data,
        queryClient,
        candidateId,
        reset,
        formatInitialValues,
        trackCitizenshipDelete: trackCitizenshipCountryRemoval,
        citizenships: getValues().citizenships,
      })
    },
  })

  const validateCountry = (foundCountry, index) => {
    // reset country value after clearing single select filter
    clearErrors()
    if (foundCountry && !foundCountry.label) {
      setValue(`regionalCitizenships.${index}.country`, '')
      setValue(`regionalCitizenships.${index}.countryName`, '')
      setValue(`regionalCitizenships.${index}.countryIso2Code`, '')
    }
    if (foundCountry && !foundCountry.value) {
      setError(`regionalCitizenships.${index}.country`, {
        type: ['error'],
        message: 'Required',
      })
      return 'Required'
    }
    if (foundCountry && foundCountry.value) {
      const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
      const formValues = getValues()
      const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(i => i.countryIso2Code)
      const citizenshipCountryCodes = [...formValues.citizenships.map(i => i.value)]
      if (existingCountryCodes.includes(foundCountry.value) || citizenshipCountryCodes.includes(foundCountry.value)) {
        const msg = 'This country has already been added. Select another country.'
        // For immediate feedback onChange
        setError(`regionalCitizenships.${index}.country`, {
          type: ['error'],
          message: msg,
        })
        // For submission error
        return msg
      }
      const country = allCountries.find(i => i.value === foundCountry.value)
      if (index > -1 && country) {
        setValue(`regionalCitizenships.${index}.countryIso2Code`, country.value)
        setValue(`regionalCitizenships.${index}.countryName`, country.label)
      }
    }
    return undefined
  }

  const submitUpdateForm = async () => {
    const formDataToUpdate = await formatFormDataForUpdate({
      formValues: getValues(),
      queryClient,
      candidateId,
    })

    await updateCitizenship(formDataToUpdate)
  }

  const submitForm = async ({ shouldSubmitCitizenships, shouldUpdate, shouldDelete }) => {
    const formWorkAuthData = formatWorkAuthDataForCreate({
      formValues: getValues(),
      queryClient,
      candidateId,
    })

    if (shouldUpdate) {
      await submitUpdateForm()
    }
    if (shouldSubmitCitizenships) {
      const formCitizenshipData = formatCitizenshipDataForCreate({
        formValues: getValues(),
        queryClient,
        candidateId,
        allCountries,
      })
      await createCitizenship([...formWorkAuthData, ...formCitizenshipData])
    } else {
      await createCitizenship(formWorkAuthData)
    }

    if (shouldDelete) {
      if (citizenshipsToDelete.length > 0) {
        await deleteCitizenships(citizenshipsToDelete)
        setCitizenshipsToDelete([])
      }
    }
  }

  const deleteCitizenship = regionalCitizenship => {
    const { id, countryName, citizenshipStatus } = regionalCitizenship
    trackCitizenshipDeleteButton({
      countryName,
      citizenshipStatus,
      citizenshipId: id,
    })
    deleteCitizenshipMutate({ id })
  }

  const onCitizenshipCountrySearch = term => {
    setFilteringCitizenshipCountries(true)
    const filteredResults = allCountries.filter(res => res.label.toLowerCase().includes(term.toLowerCase()))
    filteredCitizenshipCountryResults.current = filteredResults
    setFilteringCitizenshipCountries(false)
  }

  const selectedCitizenshipCountries = useRef([])

  const updateSelectedCitizenshipCountries = value => {
    selectedCitizenshipCountries.current = value
  }
  const citizenshipFormValues = watch('citizenships').filter(entry => !!entry.value)

  const trackAddCitizenshipCountry = response => {
    const selectedCitizenships = Object.values(response).filter(res => res.value)
    if (!deepMatch(selectedCitizenshipCountries.current, selectedCitizenships)) {
      const newCountry =
        selectedCitizenships.length > citizenshipFormValues.length ? selectedCitizenships[selectedCitizenships.length - 1] : null
      if (newCountry) {
        trackCitizenshipCountryAddition(newCountry.label)
      }
      updateSelectedCitizenshipCountries(selectedCitizenships)
    }
  }

  const shouldRemoveCitizenshipIdFromDeleteArray = response => {
    const selectedCitizenships = Object.values(response).filter(res => res.value)
    if (!deepMatch(selectedCitizenshipCountries.current, selectedCitizenships)) {
      const newCountry =
        selectedCitizenships.length > citizenshipFormValues.length ? selectedCitizenships[selectedCitizenships.length - 1] : null
      const citizenshipsToDeleteCopy = [...citizenshipsToDelete]
      if (newCountry) {
        const exists = regionalCitizenships.find(
          citizenship => citizenship.citizenshipStatus === 'CITIZEN' && citizenship.countryIso2Code === newCountry.value,
        )
        if (exists && citizenshipsToDelete.includes(exists.id)) {
          citizenshipsToDeleteCopy.splice(citizenshipsToDelete.indexOf(exists.id), 1)
          setCitizenshipsToDelete(citizenshipsToDeleteCopy)
        }
      }
    }
  }

  const handleCitizenshipCountryError = response => {
    const selectedCitizenships = Object.values(response).filter(res => res.value)
    const formValues = getValues()

    const lastSelectedCitizenship = selectedCitizenships[selectedCitizenships.length - 1]

    if (lastSelectedCitizenship) {
      const existingCountryCodes = formValues.regionalCitizenships.map(regionalCitizenship => regionalCitizenship.countryIso2Code)
      if (existingCountryCodes.includes(lastSelectedCitizenship.value)) {
        const errorMessage = 'This country has already been added. Select another country.'
        // For immediate feedback onChange
        setError('citizenships', {
          type: 'error-duplicate-country',
          message: errorMessage,
        })
        setValue(`citizenships.${selectedCitizenships.length - 1}.theme`, 'error')
        // For submission error
        return errorMessage
      }
    }
    return undefined
  }

  const onCitizenshipOptionChange = response => {
    clearErrors()
    shouldRemoveCitizenshipIdFromDeleteArray(response)
    trackAddCitizenshipCountry(response)
    handleCitizenshipCountryError(response)
  }

  const onCitizenshipTagRemove = country => {
    const regionalCitizenshipToDelete = regionalCitizenships.find(
      regionalCitizenship => regionalCitizenship.countryName === country.label && regionalCitizenship.citizenshipStatus === 'CITIZEN',
    )
    if (regionalCitizenshipToDelete) {
      const citizenshipsToDeleteCopy = [...citizenshipsToDelete]
      if (!citizenshipsToDeleteCopy.includes(regionalCitizenshipToDelete.id)) {
        citizenshipsToDeleteCopy.push(regionalCitizenshipToDelete.id)
        trackCitizenshipDeleteButton({
          countryName: regionalCitizenshipToDelete.countryName,
          citizenshipStatus: regionalCitizenshipToDelete.citizenshipStatus,
          citizenshipId: regionalCitizenshipToDelete.id,
        })
        setCitizenshipsToDelete(citizenshipsToDeleteCopy)
      }
    } else {
      trackCitizenshipDeleteButton({
        countryName: country.label,
        citizenshipStatus: 'CITIZEN',
        citizenshipId: '',
      })
    }
  }

  const formSubmitHandler = async (_, event) => {
    const newWorkAuthButtonClicked =
      event && event.nativeEvent && event.nativeEvent.submitter && event.nativeEvent.submitter.id === 'work-auth-submit-btn'
    if (newWorkAuthButtonClicked) {
      setIsLoadingSubmitNewWorkAuth(true)
      await submitForm({
        shouldUpdate: false,
        shouldSubmitCitizenships: false,
        shouldDelete: false,
      })
      setNewCitizenship(false)
      setIsLoadingSubmitNewWorkAuth(false)
    } else {
      await submitForm({
        shouldUpdate: true,
        shouldSubmitCitizenships: true,
        shouldDelete: true,
      })
      goToNextStep()
    }
  }

  const formChangeHandler = () => formError && clearErrors()

  const handleToggleEditing = regionalCitizenship => {
    updateFormState({
      formValues: getValues(),
      queryClient,
      candidateId,
      reset,
      citizenshipsToDelete,
    })
    const editingCountryCodeIndex = editingCitizenshipIds.indexOf(regionalCitizenship.id)
    const countryCodesCopy = [...editingCitizenshipIds]
    if (editingCountryCodeIndex > -1) {
      countryCodesCopy.splice(editingCountryCodeIndex, 1)
    } else {
      trackCitizenshipEditButton({
        countryName: regionalCitizenship.countryName,
        citizenshipId: regionalCitizenship.id,
        citizenshipStatus: regionalCitizenship.citizenshipStatus,
      })
      countryCodesCopy.push(regionalCitizenship.id)
    }
    setEditingCitizenshipids(countryCodesCopy)
  }

  const handleToggleAdding = () => {
    updateFormState({
      formValues: getValues(),
      queryClient,
      candidateId,
      reset,
      citizenshipsToDelete,
    })

    if (newCitizenship) {
      remove(controlledFields.length)
      setNewCitizenship(false)
    } else {
      trackCitizenshipAddButton()
      append({
        countryName: '',
        countryIso2Code: '',
        citizenshipStatus: '',
        candidateProfileId: candidateId,
      })
      setNewCitizenship(true)
    }
  }

  const countryListLast = preferredCountries[preferredCountries.length - 1]
  const countryListFirst = preferredCountries.slice(0, preferredCountries.length - 1)

  return {
    createCitizenship,
    deleteCitizenship,
    formSubmitHandler,
    formChangeHandler,
    handleSubmit,
    handleToggleAdding,
    handleToggleEditing,
    onCitizenshipCountrySearch,
    onCitizenshipOptionChange,
    onCitizenshipTagRemove,
    submitUpdateForm,
    setNewCitizenship,
    validateCountry,
    allCountries,
    control,
    controlledFields,
    countryListFirst,
    countryListLast,
    editingCitizenshipIds,
    errors,
    filteringCitizenshipCountries,
    filteredCitizenshipCountryResults,
    formError,
    isCreatingCitizenships,
    isDeletingCitizenship,
    isDeletingCitizenships,
    isLoadingCountries,
    isLoadingSubmitNewWorkAuth,
    isUpdatingCitizenship,
    newCitizenship,
    selectedCountries,
  }
}
